import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  QuestionFrequency,
  QuestionFrequencyData,
  QuestionFrequencyDataType,
  Question,
} from '@/types/api';
import { Paths } from '@/types/routes';
import { weekPeriodByDate } from '@/utils/format';
import RotatingQuestionItemComponent from './RotatingQuestionItem.component';

type RotatingQuestionItemProps = {
  question: Question;
  handleDeleteQuestion: (questionId: string | number) => void;
};

const RotatingQuestionItem = function ({
  question,
  handleDeleteQuestion,
}: RotatingQuestionItemProps): JSX.Element {
  const navigate = useNavigate();

  const [buttonMenuElement, setButtonMenuElement] = useState<null | HTMLElement>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const handleOpenDialog = (): void => {
    setButtonMenuElement(null);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDialog = (): void => {
    setIsDeleteDialogOpen(false);
  };

  const handleToggleButtonMenu = (event: any | null): void => {
    if (!event) {
      setButtonMenuElement(null);
      return;
    }

    setButtonMenuElement((currentElement) =>
      currentElement === null ? event.currentTarget : null
    );
  };

  const handleEditQuestion = useCallback((): void => {
    navigate(Paths.MANAGE_ROTATING_QUESTION, { state: { question } });
  }, [navigate, question]);

  const handleClickOnDelete = useCallback((): void => {
    handleDeleteQuestion(question.id);
    handleCloseDialog();
  }, [handleDeleteQuestion, question.id]);

  const getFrequencyData = (often?: QuestionFrequency): QuestionFrequencyDataType | undefined => {
    if (!often) return undefined;

    const oftenThatShowLabel = [QuestionFrequency.ONE_TIME_QUESTION, QuestionFrequency.SCHEDULED];

    if (oftenThatShowLabel.includes(often)) {
      return QuestionFrequencyData[often];
    }

    return undefined;
  };

  const frequencyData = useMemo(
    () => getFrequencyData(question.questionFrequency),
    [question.questionFrequency]
  );

  const [nextCheckinStartDate, nextCheckinEndDate] = useMemo(
    () => (question.scheduledAt ? weekPeriodByDate(question.scheduledAt) : [null, null]),
    [question.scheduledAt]
  );

  return (
    <RotatingQuestionItemComponent
      frequency={frequencyData}
      question={question.label}
      nextCheckinStartDate={nextCheckinStartDate}
      nextCheckinEndDate={nextCheckinEndDate}
      handleToggleButtonMenu={handleToggleButtonMenu}
      buttonMenuElement={buttonMenuElement}
      isDeleteDialogOpen={isDeleteDialogOpen}
      handleOpenDialog={handleOpenDialog}
      handleCloseDialog={handleCloseDialog}
      handleEditQuestion={handleEditQuestion}
      handleClickOnDelete={handleClickOnDelete}
    />
  );
};

export default RotatingQuestionItem;
